<template>
  <div class="offers-container">
    <h2 class="title">{{ $t("offers.offers") }}</h2>
    <div v-if="offers.length === 0" class="no-offers">
      <p data-cy="no-offers-text">{{ $t("offers.no-pending") }}</p>
    </div>
    <div v-for="(offer, index) in offers" :key="index" class="offer-item">
      <div class="offer-info">
        <div class="offer-title">{{ offer.priceName }}</div>
        <div class="offer-discount">
          {{ offer.title }} {{ offer.discount }}%
        </div>
      </div>
      <SInputGroup class="offer-price">
        <SInputGroupAddon align="inline-start">
          {{ bookingCurrency }}
        </SInputGroupAddon>
        <SInputGroupInput
          :model-value="offer.amount"
          @update:model-value="(v) => (offer.amount = String(v))"
          disabled
        />
      </SInputGroup>
      <SButton
        type="button"
        variant="outline"
        size="icon-sm"
        class="remove-btn rounded-full"
        :disabled="!offer.stillPending"
        aria-label="remove offer"
        data-cy="offer-remove-btn"
        @click="confirmRemovingOffer(offer)"
      >
        <XIcon style="font-size: 16px; color: red" />
      </SButton>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  h,
  onMounted,
  ref,
  watch,
  type Component,
} from "vue";
import { useI18n } from "vue-i18n";
import closeIcon from "@/assets/images/svg/close.svg";
import BookingModule from "@/store/modules/booking";
import { IProductSlotBooking } from "@/types/db/product-slot-booking";
import {
  AccountingState,
  IAccounting,
  PaymentMethods,
  TransactionTypes,
} from "@/types/db/accounting";
import OffersModule from "@/store/modules/offers";
import { sumArray } from "@/utils/helpers/common";
import {
  BookingStatus,
  IQuantifiedProductSlotState,
} from "@/types/db/product-slot-state";
import { getPSSValue } from "@/utils/helpers/product-slot-state";
import { isOfferAccountingRefunded } from "@/utils/helpers/offers";
import { groupedPSS, updateEnteredAmount } from "@/utils/vue-helpers/booking";
import { LogService } from "@/services/logging";
import { cloneDeep } from "lodash";
import { IBookingsSteps } from "@/interfaces/store/IBooking";
import { BookingStep } from "@/constants/booking";
import { restToPay } from "@/utils/vue-helpers/new-accountings";
import {
  SButton,
  SInputGroup,
  SInputGroupAddon,
  SInputGroupInput,
} from "@smeetz/ui-core";
import { XIcon } from "@smeetz/ui-icons";
import { showConfirm } from "@/utils/vue-helpers/confirm-dialog";

interface IAppliedOffer {
  offerId: number;
  title: string;
  priceName: string;
  priceId: number;
  pssUuids: string[];
  discount: string;
  amount: string;
  stillPending: boolean;
}
export default defineComponent({
  name: "offers",
  components: {
    SButton,
    SInputGroup,
    SInputGroupAddon,
    SInputGroupInput,
    XIcon,
  },
  setup() {
    const { t } = useI18n({});

    const offers = ref<IAppliedOffer[]>([]);

    const bookingCurrency = computed(() => {
      const booking = BookingModule.selectedBooking;
      if (!booking) return "";
      const currency = booking.currency;
      return currency;
    });

    /**
     * Builds the rich body shown inside the remove-offer confirmation.
     * Returns a one-shot Component bound to the offer being removed.
     *
     * @param {IAppliedOffer} offer - The offer the user is about to remove.
     * @returns {Component} Vue component to pass as `showConfirm({ body })`.
     */
    function buildRemoveOfferBody(offer: IAppliedOffer): Component {
      const currency = bookingCurrency.value;
      return {
        render: () =>
          h("div", { class: "flex flex-col gap-4" }, [
            h(
              "p",
              { class: "text-sm text-muted-foreground" },
              t("offers.confirm-remove-msg")
            ),
            h(
              "div",
              {
                class:
                  "flex items-center justify-between gap-4 rounded-md border border-gray-200 p-4",
              },
              [
                h("div", { class: "min-w-0 flex-1" }, [
                  h(
                    "div",
                    {
                      class: "truncate text-base font-semibold text-foreground",
                    },
                    offer.priceName
                  ),
                  h(
                    "div",
                    { class: "text-xs text-muted-foreground" },
                    `${offer.title} ${offer.discount}%`
                  ),
                ]),
                h(
                  "div",
                  { class: "text-sm font-medium tabular-nums" },
                  `${currency} ${offer.amount}`
                ),
              ]
            ),
          ]),
      };
    }
    /**
     * Finds discounts and promocodes applied on a ticket that are part of offers
     * Value returned is fully based on accountings
     * e.g: Offer ("Promocode" or "Discount") 50% / CHF 6.00 -applied
     */
    function getTicketAppliedOffers(
      booking: IProductSlotBooking,
      pssGroup: IQuantifiedProductSlotState
    ): IAppliedOffer[] {
      const unacceptedPSSStates = [
        BookingStatus.Canceled,
        BookingStatus.Confirmed,
        BookingStatus.Abandoned,
      ];
      if (
        pssGroup.bookingStatus &&
        unacceptedPSSStates.includes(pssGroup.bookingStatus)
      ) {
        // if the PSS is canceled, confirmed or abandoned, don't allow removing the offer
        return [];
      }
      const offerAccountings = booking.accountings.filter(
        (acc) =>
          acc.refund !== 1 &&
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT &&
          (acc.paymentMethod === PaymentMethods.METHOD_OFFER ||
            acc.paymentMethod === PaymentMethods.METHOD_POS_OFFER) &&
          (acc.state === AccountingState.Completed ||
            acc.state === AccountingState.Pending)
      );

      const offerRefundAccountings = booking.accountings.filter(
        (acc) =>
          acc.refund === 1 &&
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT &&
          (acc.paymentMethod === PaymentMethods.METHOD_OFFER ||
            acc.paymentMethod === PaymentMethods.METHOD_POS_OFFER) &&
          acc.state === AccountingState.Completed
      );

      if (
        !offerAccountings ||
        !offerAccountings.length ||
        !offerRefundAccountings
      ) {
        // there are no offer accountings
        return [];
      }

      const rowPssListUuids = pssGroup.pssIds;
      const pssGroupPricesSum = pssGroup.quantity * getPSSValue(pssGroup);
      const appliedOffers: IAppliedOffer[] = [];
      const refundAccountings: (string | null)[] = [];
      for (const acc of offerAccountings) {
        const offerAes = acc.accountingEntriesList.filter((ae) =>
          rowPssListUuids.some(
            (element) => element === ae.productSlotStateUuid && ae.value > 0
          )
        );
        if (!offerAes || !offerAes.length) continue;
        // check if accounting has been refunded, in which case don't include it in the summary if the pss group is confirmed
        const refundAcc =
          pssGroup.bookingStatus === BookingStatus.Confirmed
            ? isOfferAccountingRefunded(
                acc,
                offerRefundAccountings,
                refundAccountings
              )
            : null;
        if (refundAcc) {
          refundAccountings.push(refundAcc.uuid);
          continue;
        }
        const pssGroupDiscountsSum = sumArray(offerAes.map((it) => it.value));
        const newPriceSum = pssGroupPricesSum - pssGroupDiscountsSum;
        let offer: IAppliedOffer | null = null;
        if (acc.promocode) {
          offer = {
            offerId: acc.accountingEntriesList[0].offerId || 0,
            title: String(acc.promocode),
            discount: (
              ((pssGroupPricesSum - newPriceSum) / pssGroupPricesSum) *
              100
            ).toFixed(0),
            amount: ((pssGroupPricesSum - newPriceSum) / 100).toFixed(2),
            priceName: pssGroup.priceName,
            priceId: pssGroup.priceId,
            pssUuids: pssGroup.pssIds,
            stillPending: acc.state === AccountingState.Pending,
          };
        } else {
          // use the offer name or a generic "Discount Offer" if not found
          const offerName =
            (OffersModule.offersCommon as any)?.offers?.find(
              (offer: any) => offer.id === offerAes[0].offerId
            )?.name || t("label.offer-discount");
          offer = {
            offerId: acc.accountingEntriesList[0].offerId || 0,
            title: offerName,
            discount: (
              ((pssGroupPricesSum - newPriceSum) / pssGroupPricesSum) *
              100
            ).toFixed(0),
            amount: ((pssGroupPricesSum - newPriceSum) / 100).toFixed(2),
            priceName: pssGroup.priceName,
            priceId: pssGroup.priceId,
            pssUuids: pssGroup.pssIds,
            stillPending: acc.state === AccountingState.Pending,
          };
        }
        appliedOffers.push(offer);
      }
      return appliedOffers;
    }

    async function confirmRemovingOffer(offer: IAppliedOffer) {
      const confirmed = await showConfirm({
        title: t("offers.remove-offer"),
        body: buildRemoveOfferBody(offer),
        variant: "destructive",
        confirmText: t("offers.yes-remove"),
      });
      if (confirmed) {
        LogService.info(`User confirmed pending offer removal`, {
          params: offer,
        });
        removePendingOffer(offer);
      } else {
        LogService.info(`User did not confirm offer cancellation`, {
          params: offer,
        });
      }
    }

    function removePendingOffer(offer: IAppliedOffer) {
      // OffersModule.clearBookingOffers(booking.uuid as string);
      // BookingModule.removeOfferPromocodes(booking.uuid as string);

      const bookingCopy = cloneDeep(BookingModule.selectedBooking);
      if (!bookingCopy) return;
      const newAccountings: IAccounting[] = [];

      const linkedPSS = offer.pssUuids;

      for (let i = 0; i < bookingCopy.accountings.length; i++) {
        const accounting = bookingCopy.accountings[i];

        if (
          accounting.paymentMethod === PaymentMethods.METHOD_POS_OFFER &&
          !accounting.id &&
          accounting.state === AccountingState.Pending &&
          accounting.accountingEntriesList.some(
            (ae) =>
              ae.productSlotStateUuid &&
              linkedPSS.includes(ae.productSlotStateUuid) &&
              ae.value > 0 &&
              ae.offerId === offer.offerId
          )
        ) {
          // if the accounting is a pending offer accounting, remove accounting entries linked to the PSS
          // if the accounting becomes empty afterwards, remove it completely.
          accounting.accountingEntriesList =
            accounting.accountingEntriesList.filter(
              (ae) =>
                ae.productSlotStateUuid &&
                !linkedPSS.includes(ae.productSlotStateUuid)
            );
          if (accounting.accountingEntriesList.length === 0) {
            continue;
          }
        }

        newAccountings.push(accounting);
      }

      bookingCopy.accountings = newAccountings;

      BookingModule.updateBooking(
        bookingCopy // update the booking with the new accountings
      );

      // reload the applied offers list in the UI
      getAppliedPendingOffers(bookingCopy);

      BookingModule.setBookingStep({
        bookingUUID: BookingModule.selectedBookingUUID,
        step: BookingStep.Offers,
      });

      // update the rest to pay in payment step
      updateEnteredAmount(Math.abs(restToPay.value) / 100);
    }

    function getAppliedPendingOffers(booking: IProductSlotBooking) {
      offers.value = [];

      const groupedPssList = groupedPSS(booking.pssList);

      groupedPssList.forEach((pssGroup) => {
        const appliedOffers = getTicketAppliedOffers(booking, pssGroup) || [];
        offers.value.push(
          ...appliedOffers.filter((offer) => offer.stillPending)
        );
      });
    }

    onMounted(() => {
      const booking = BookingModule.selectedBooking;
      if (!booking) return;

      getAppliedPendingOffers(booking);
    });

    return {
      t,
      offers,
      confirmRemovingOffer,
      closeIcon,
      bookingCurrency,
    };
  },
});
</script>

<style lang="scss" scoped>
.offers-container {
  height: 90%;
  min-width: 600px;
  padding: 1em 0;
  font-size: 16px;
}

.offer-item {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.offer-info {
  flex: 1;
}

.offer-title {
  font-weight: medium;
  font-size: 16px;
}

.offer-discount {
  color: #888;
  font-size: 12px;
}

.offer-price {
  width: 300px;
  margin-right: 10px;
}

.title {
  margin-bottom: 30px;
  font-size: 24px;
  font-weight: 500;
}

.remove-btn {
  border: 2px solid red;
  background-color: white;
  color: red;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.remove-btn:hover {
  background-color: #ffe5e5;
}
.no-offers {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: 18px;
  color: #888;
}
::v-deep .el-input-group__prepend {
  color: black;
}
::v-deep .el-input__inner {
  color: black;
  -webkit-text-fill-color: black !important;
}
</style>
<style>
.btn-confirm-rmve-offer,
.btn-confirm-rmve-offer:hover {
  background-color: #ef4444;
}
.confirm-remove-offer {
  min-width: 600px;
}
.confirm-remove-offer .el-input-group__prepend {
  color: black;
}
.confirm-remove-offer .el-input__inner {
  color: black;
  -webkit-text-fill-color: black !important;
}
</style>
